import * as React from 'react';
import { Async } from '@uifabric/utilities/lib/Async';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { now } from '../../utilities/performance';

/**
 * Let's get some comments about this interface and some
 * js docs comments about each of the members
 * @public
 */
export interface ILayoutRenderProps {
    onLayoutRenderComplete: (pageLoadTime: number) => void;
    isDataReadyForLayout: boolean;
}

/**
 * @public
 */
export type IDecorateWithLayoutRenderCompleteProps<TProps> = IDecoratedProps<TProps, {}, ILayoutRenderProps>;

/**
 * No idea what this HOC does... Can we get some detailed
 * comments here about what this is doing.
 *
 * Also, let's get some comments in the code below to make things
 * crystal clear abut what's going on.
 * @internal
 */
export function withLayoutRenderComplete(): IComponentDecorator<{}, ILayoutRenderProps> {
    return <P extends {}>(
        component: React.ComponentType<P>
    ): IDecoratedComponentType<P, {}, ILayoutRenderProps> => {
        interface ILayoutTrackerState {
            // Nothing.
        }

        const Component = component;

        class LayoutTracker extends React.PureComponent<P & ILayoutRenderProps, ILayoutTrackerState> {
            private _async: Async;

            constructor(props: P & ILayoutRenderProps) {
                super(props);

                this._async = new Async(this);

                this.state = {};
            }

            public render(): JSX.Element {
                return (
                    <Component
                        { ...this.props }
                    />
                );
            }

            public componentDidMount(): void {
                if (this._isReady(this.props, this.state)) {
                    this._async.setTimeout(() => {
                        this.props.onLayoutRenderComplete(now());
                    }, 0);
                }
            }

            public componentDidUpdate(previousProps: Readonly<P & ILayoutRenderProps>, previousState: ILayoutTrackerState): void {
                if (!this._isReady(previousProps, previousState) && this._isReady(this.props, this.state)) {
                    this._async.setTimeout(() => {
                        this.props.onLayoutRenderComplete(now());
                    }, 0);
                }
            }

            public componentWillUnmount(): void {
                this._async.dispose();
            }

            private _isReady(props: Readonly<P & ILayoutRenderProps>, state: ILayoutTrackerState): boolean {
                if (props.isDataReadyForLayout) {
                    return true;
                }

                return false;
            }
        }

        // @ts-ignore
        return LayoutTracker;
    };
}
